import { Claims } from '@database/types';

/**
 * Checks if a redirect path is safe (same-origin, no protocol-relative, no /login).
 * Handles the lowercase /login check and all startsWith checks.
 */
function isSafeRedirectPath(path: string): boolean {
  const lowerPath = path.toLowerCase();
  return (
    path.startsWith('/') &&
    !path.startsWith('//') &&
    !path.startsWith('/\\') &&
    !path.startsWith('\\') &&
    !lowerPath.startsWith('/login')
  );
}

/**
 * Validates a redirect path without decoding.
 * Use for internally constructed paths to preserve percent-encoding.
 * Only allows same-origin paths (must start with '/').
 * Returns true if the path is safe, false otherwise.
 */
export function validateRedirectPath(path: string): boolean {
  if (!path || typeof path !== 'string') return false;
  const pathOnly = path.split('?')[0];
  return isSafeRedirectPath(pathOnly);
}

/**
 * Safely decodes and validates a redirect path from route.query.redirect.
 * Only allows same-origin paths (must start with '/').
 * Returns the validated path or undefined if decoding/validation fails.
 */
export function decodeAndValidateRedirectPath(raw: string | string[] | undefined): string | undefined {
  if (raw == null) return undefined;
  const str = typeof raw === 'string' ? raw : Array.isArray(raw) ? raw[0] : undefined;
  if (typeof str !== 'string' || !str) return undefined;
  try {
    const decoded = decodeURIComponent(str);
    return isSafeRedirectPath(decoded) ? decoded : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Ensures a URL ends with a specific version.
 * Concierge environment variables are versioned but Valet is not.
 * This handles that difference when Valet & Concierge use same components.
 */
export const ensureUrlVersion = (url: string, version = 'v1') => {
  return !String(url).endsWith('/v1') ? `${url}/${version}` : url;
};

export function extractIdFromShopifyUrl(url: string): string | null {
  if (!url) return null;

  try {
    const parsedUrl = new URL(url);
    const pathname = parsedUrl.pathname;

    // Match patterns like: /discounts/[id] or /gift_cards/[id]
    const discountMatch = pathname.match(/\/discounts\/([^/]+)$/);
    const giftCardMatch = pathname.match(/\/gift_cards\/([^/]+)$/);

    const id = discountMatch?.[1] || giftCardMatch?.[1];

    if (!id) {
      return null;
    }

    return decodeURIComponent(id);
  } catch {
    // If the URL is invalid or cannot be parsed, fall back to null
    return null;
  }
}

export function getStoreCreditTypeFromUrl(url: string): Claims.StoreCreditType | undefined {
  if (!url) return undefined;

  try {
    const parsedUrl = new URL(url);
    const pathname = parsedUrl.pathname;

    if (/\/gift_cards\/[^/]+$/.test(pathname)) {
      return Claims.StoreCreditType.GIFT_CARD;
    }

    if (/\/discounts\/[^/]+$/.test(pathname)) {
      return Claims.StoreCreditType.DISCOUNT_CODE;
    }
  } catch {
    // If the URL is invalid or cannot be parsed, fall through to undefined
  }

  return undefined;
}
